import { assertDefined, assertEnvVar, assertStr } from '@h5web/app';
import { useSuspenseQuery } from '@tanstack/react-query';
import { type AuthContextProps, useAuth } from 'react-oidc-context';

const ICATPLUS_URL = import.meta.env.VITE_ICATPLUS_URL;
const ANONYMOUS = { plugin: 'db', username: 'reader', password: 'reader' };

export function useSessionId(): string {
  assertEnvVar(ICATPLUS_URL, 'VITE_ICATPLUS_URL');

  const auth = useAuth() as AuthContextProps | undefined; // `AuthProvider` only on private routes (cf. `main.tsx`)

  // If this is a private route, automatic sign-in should have been performed
  if (auth && !auth.isAuthenticated) {
    throw new Error('Not signed in');
  }

  return useSuspenseQuery({
    queryKey: ['session'], // don't include SSO token to avoid refecthing long-lived ICAT session ID unnecessarily
    queryFn: async () => {
      const token = auth?.user?.access_token;

      const response = await fetch(`${ICATPLUS_URL}/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(
          token ? { plugin: 'esrf', password: token } : ANONYMOUS,
        ),
      });

      if (!response.ok) {
        throw new Error("Couldn't sign into ICAT", {
          cause: await response.text(),
        });
      }

      const { sessionId } = await response.json();
      assertDefined(sessionId);
      assertStr(sessionId);

      return sessionId;
    },
    refetchInterval: 60 * 60 * 1000, // 60 min
    refetchIntervalInBackground: true,
  }).data;
}
